import React, { useState, useEffect } from "react";
import { Card, Button, Input, Form, message, Tag, Drawer, Popconfirm, Collapse, Image } from "antd";
import {
  Youtube,
  Instagram,
  Video,
  Twitter,
  Download,
  Share2,
  TrendingUp,
  MessageCircle,
  CheckCircle,
  Trash2,
  ExternalLink,
  FileText,
  Users,
  Award,
  DollarSign,
} from "lucide-react";
import { getReferralLink } from "../utils/referralHelpers";
import { _submit_ambassador_posts, _delete_ambassador_post, _get_ambassador_next_post, _get_ambassador_active_promo_assets, _get_ambassador_today_post_status } from "../common/axios_services";
import buildAdminCollapseItem, { AdminCollapseList } from "./admin/AdminCollapseItem";

const AmbassadorPerformance = ({
  ambassadorInfo = {},
  performanceStats = {},
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReferralDrawerOpen, setIsReferralDrawerOpen] = useState(false);
  const [isPostDrawerOpen, setIsPostDrawerOpen] = useState(false);
  const [deletingPost, setDeletingPost] = useState(null);
  const [existingPosts, setExistingPosts] = useState(
    ambassadorInfo?.ambassadorSubmittedPosts || ambassadorInfo?.topPosts || []
  );
  const [nextPost, setNextPost] = useState(null);
  const [activePromo, setActivePromo] = useState(null);
  const [todayPostStatus, setTodayPostStatus] = useState({ hasSubmittedToday: false });
  const hasSubmittedToday = Boolean(todayPostStatus?.hasSubmittedToday);

  const isApproved = ambassadorInfo?.isApproved !== false && (!ambassadorInfo?.status || ambassadorInfo?.status === "APPROVED");

  useEffect(() => {
    if (!isApproved) return;

    if (Array.isArray(ambassadorInfo?.ambassadorSubmittedPosts)) {
      setExistingPosts(ambassadorInfo.ambassadorSubmittedPosts);
    } else if (Array.isArray(ambassadorInfo?.topPosts)) {
      setExistingPosts(ambassadorInfo.topPosts);
    }

    const fetchAmbassadorContent = async () => {
      try {
        const [nextPostRes, promoRes, statusRes] = await Promise.all([
          _get_ambassador_next_post(),
          _get_ambassador_active_promo_assets(),
          _get_ambassador_today_post_status(),
        ]);
        setNextPost(nextPostRes?.data || null);
        setActivePromo(promoRes?.data || null);
        setTodayPostStatus(statusRes?.data || { hasSubmittedToday: false });
      } catch (err) { }
    };
    fetchAmbassadorContent();

    if (window.location.hash.includes("submitted-promotional-posts") || window.location.hash.includes("latest-promo-post")) {
      setTimeout(() => {
        const el = document.getElementById("submitted-promotional-posts") || document.getElementById("latest-promo-post");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    }
  }, [ambassadorInfo?.ambassadorSubmittedPosts, ambassadorInfo?.topPosts]);

  const handlePostSubmit = async (values) => {
    const validValues = Object.fromEntries(
      Object.entries(values || {}).filter(([_, v]) => v && String(v).trim() !== "")
    );

    if (Object.keys(validValues).length === 0) {
      message.error("Please provide at least one post link to submit.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await _submit_ambassador_posts(validValues);
      message.success(res?.data?.message || "Post link submitted successfully and approved!");
      if (Array.isArray(res?.data?.submittedPosts)) {
        setExistingPosts(res.data.submittedPosts);
      } else if (Array.isArray(res?.data?.topPosts)) {
        setExistingPosts(res.data.topPosts);
      } else {
        const newUrls = Object.values(validValues);
        setExistingPosts((prev) => Array.from(new Set([...prev, ...newUrls])));
      }
      setTodayPostStatus((current) => ({ ...current, hasSubmittedToday: true, submittedAt: new Date().toISOString() }));
      form.resetFields();
      setIsPostDrawerOpen(false);
      window.setTimeout(() => {
        document.getElementById("submitted-promotional-posts")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to submit post links.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (target) => {
    const targetKey = typeof target === "object" ? target.id || target.postUrl : target;
    setDeletingPost(targetKey);
    try {
      const res = await _delete_ambassador_post(targetKey);
      message.success(res?.data?.message || "Post link deleted successfully.");
      if (Array.isArray(res?.data?.submittedPosts)) {
        setExistingPosts(res.data.submittedPosts);
      } else {
        setExistingPosts((prev) =>
          prev.filter((p) => {
            const url = typeof p === "object" ? p.postUrl : p;
            const id = typeof p === "object" ? p.id : null;
            return url !== targetKey && id !== targetKey;
          })
        );
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete post link.");
    } finally {
      setDeletingPost(null);
    }
  };

  const whatsappLink = "https://wa.me/2348061107627?text=Hello%20Konnect%20Ambassador%20Team";

  const getPlatformMeta = (url = "") => {
    const l = url.toLowerCase();
    if (l.includes("instagram.com")) return { name: "Instagram", icon: <Instagram className="w-4 h-4 text-pink-600" /> };
    if (l.includes("tiktok.com")) return { name: "TikTok", icon: <Video className="w-4 h-4 text-black" /> };
    if (l.includes("x.com") || l.includes("twitter.com")) return { name: "Twitter / X", icon: <Twitter className="w-4 h-4 text-blue-500" /> };
    if (l.includes("youtube.com") || l.includes("youtu.be")) return { name: "YouTube", icon: <Youtube className="w-4 h-4 text-red-600" /> };
    return { name: "Social Post", icon: <ExternalLink className="w-4 h-4 text-gray-600" /> };
  };

  const referralLink = getReferralLink(ambassadorInfo?.referralCode || ambassadorInfo?.code || ambassadorInfo?.accountId || "MY_CODE");

  const todayPost = nextPost?.todayPost || (nextPost?.title ? nextPost : null);
  const tomorrowPost = nextPost?.tomorrowPost || null;
  const promoAssets = [
    activePromo?.flierUrl ? { label: "Promo flier", url: activePromo.flierUrl } : null,
    ...(Array.isArray(activePromo?.flierUrls) ? activePromo.flierUrls : [])
      .filter((url) => Boolean(url) && url !== activePromo?.flierUrl)
      .map((url, index) => ({ label: `Promo flier ${index + 2}`, url })),
    ...Object.entries(activePromo?.audienceImageUrls || {})
      .filter(([, url]) => Boolean(url))
      .map(([audience, url]) => ({ label: `${audience.charAt(0)}${audience.slice(1).toLowerCase()} asset`, url })),
  ].filter(Boolean);

  const rawPostItems = [
    todayPost ? { ...todayPost, isToday: true, label: "Today's Post: Campaign Task", tag: "TODAY'S POST LIVE ACTIVE" } : null,
    tomorrowPost ? { ...tomorrowPost, isTomorrow: true, label: "Tomorrow's Post Preview", tag: "UPCOMING POST" } : null,
  ].filter(Boolean);

  const postItems = hasSubmittedToday
    ? [tomorrowPost || todayPost].filter(Boolean)
    : (rawPostItems.length > 0 ? rawPostItems : [todayPost].filter(Boolean));

  const renderAmbassadorPostCollapseItems = () => {
    const rawList = postItems.length > 0 ? postItems : (nextPost ? [nextPost] : []);

    return rawList.map((postItem, idx) => {
      const isToday = !hasSubmittedToday;
      const referralLinkToUse = getReferralLink(
        ambassadorInfo.accountId || ambassadorInfo.referralCode || ambassadorInfo.code || "MY_CODE",
        "marketplace"
      );
      const cleanMessage = (postItem.caption || postItem.message || "Check out Konnect App! Sign up using my referral link: {REFERRAL_LINK}")
        .replace(/\{REFERRAL_LINK\}/g, referralLinkToUse)
        .trim();
      const channelCaptions = postItem.captions && typeof postItem.captions === "object"
        ? Object.entries(postItem.captions).filter(([, caption]) => typeof caption === "string" && caption.trim())
        : [];
      const captionEntries = channelCaptions.length > 0 ? channelCaptions : [["GENERAL", postItem.caption || postItem.message || cleanMessage]];
      const captionLabel = (channel) => ({
        GENERAL: "Post Message",
        WHATSAPP: "WhatsApp Caption",
        INSTAGRAM: "Instagram Caption",
        TIKTOK: "TikTok Caption",
        TWITTER: "X / Twitter Caption",
        YOUTUBE: "YouTube Caption",
      }[channel] || `${channel} Caption`);
      const rawInstructions = postItem.instructions || "1. Download the media asset.\n2. Copy caption with your referral link.\n3. Share on Instagram, TikTok, or WhatsApp status.\n4. Submit your post link for automatic approval.";
      const tagText = isToday ? "TODAY'S POST (LIVE ACTIVE)" : "TOMORROW'S POST (UPCOMING)";
      const tagColor = isToday ? "emerald" : "blue";
      const mediaUrls = Array.isArray(postItem.mediaUrls) && postItem.mediaUrls.length
        ? postItem.mediaUrls
        : (postItem.mediaUrl ? [postItem.mediaUrl] : []);

      const collapseFields = [
        ...(mediaUrls.length
          ? [
            {
              label: "Media Asset",
              fullWidth: true,
              value: (
                <div className="flex flex-wrap items-center gap-3 justify-start pt-1">
                  {mediaUrls.map((mediaUrl) => (
                    <div key={mediaUrl} className="flex items-center gap-2">
                      {postItem.mediaType === "VIDEO" ? (
                        <video src={mediaUrl} controls className="h-16 max-w-[160px] rounded-lg border border-gray-200" />
                      ) : (
                        <Image
                          src={mediaUrl}
                          alt="Media Asset"
                          width={60}
                          height={60}
                          className="object-cover rounded-lg border border-gray-200 shadow-xs cursor-pointer overflow-hidden"
                        />
                      )}
                      <Button
                        type="default"
                        size="small"
                        href={mediaUrl}
                        target="_blank"
                        download
                        className="font-gilroyBold rounded-lg flex items-center gap-1 cursor-pointer"
                      >
                        <Download size={14} /> Download
                      </Button>
                    </div>
                  ))}
                </div>
              ),
            },
          ]
          : []),
        ...captionEntries.map(([channel, caption]) => {
          const resolvedCaption = caption.replace(/\{REFERRAL_LINK\}/g, referralLinkToUse).trim();
          return {
            label: captionLabel(channel),
            fullWidth: true,
            value: (
              <div className="flex items-center gap-2 w-full pt-1">
                <Input
                  readOnly
                  value={resolvedCaption}
                  className="font-mono rounded-lg flex-1 bg-gray-50 border-gray-200"
                />
                <Button
                  type="primary"
                  size="small"
                  className={`${isToday ? "bg-black hover:bg-gray-800" : "bg-blue-600 hover:bg-blue-700"} text-white rounded-lg font-gilroyBold flex items-center gap-1 cursor-pointer flex-shrink-0`}
                  onClick={() => {
                    navigator.clipboard.writeText(resolvedCaption);
                    message.success(`${captionLabel(channel)} copied!`);
                  }}
                >
                  <FileText size={14} /> Copy Message
                </Button>
              </div>
            ),
          };
        }),
        {
          label: "Referral Link",
          fullWidth: true,
          value: (
            <div className="flex items-center gap-2 w-full pt-1">
              <Input
                readOnly
                value={referralLinkToUse}
                className="font-mono rounded-lg flex-1 bg-gray-50 border-gray-200 "
              />
              <Button
                type="default"
                size="small"
                className="bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-gilroyBold flex items-center gap-1 cursor-pointer border border-gray-300 flex-shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(referralLinkToUse);
                  message.success("Referral link copied!");
                }}
              >
                <Share2 size={14} /> Copy Link
              </Button>
            </div>
          ),
        },
        {
          label: "How to Upload",
          fullWidth: true,
          value: (
            <div className="text-gray-700 whitespace-pre-line leading-relaxed text-left font-sans bg-gray-50 p-3.5 rounded-xl border border-gray-200 w-full mt-1">
              {rawInstructions}
            </div>
          ),
        },
        {
          label: "Promo Brand Assets",
          fullWidth: true,
          value: (
            <div className="bg-black text-white rounded-2xl p-4 flex items-center justify-between gap-3 shadow-sm mt-1">
              <div>
                <h5 className="font-gilroyBold  text-white m-0">Download Promo Brand Assets</h5>
                <p className="text-xs text-gray-400 m-0 mt-0.5">
                  Download the official Konnect brand mark for your promotional posts.
                </p>
              </div>
              {promoAssets.length > 0 && promoAssets[0]?.url ? (
                <a
                  href={promoAssets[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-gilroyBold text-xs px-4 py-2.5 rounded-xl inline-flex items-center gap-1.5 shrink-0 no-underline transition-all cursor-pointer"
                >
                  <Download size={14} />
                  <span>Download</span>
                </a>
              ) : (
                <Button
                  className="bg-emerald-500 hover:bg-emerald-600 text-white border-0 font-gilroyBold text-xs px-4 py-2 rounded-xl inline-flex items-center gap-1.5 shrink-0 cursor-pointer"
                  onClick={() => message.info("Brand assets available soon.")}
                >
                  <Download size={14} />
                  <span>Download</span>
                </Button>
              )}
            </div>
          ),
        },
      ];

      return buildAdminCollapseItem({
        key: String(postItem.id || `post-${idx}`),
        headerTitle: `${isToday ? "Today's Post" : "Tomorrow's Post"}: ${postItem.title || "Campaign Task"}`,
        headerTagText: tagText,
        headerTagColor: tagColor,
        items: collapseFields,
      });
    });
  };

  if (!isApproved) {
    return (
      <div className="p-8 text-center bg-amber-50 border border-amber-200 rounded-3xl max-w-xl mx-auto my-8 shadow-xs">
        <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
          <Award className="w-8 h-8" />
        </div>
        <h3 className="font-gilroyBold text-2xl text-gray-900 m-0">Restricted Access</h3>
        <p className="text-gray-600 font-gilroyMedium text-base mt-2 leading-relaxed">
          Only approved ambassadors have access to the Performance page. Please complete your orientation lessons, pass the qualifying quiz, and await administrative approval.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 my-6 pb-36">
      {/* Hero Header Banner */}
      <div className="bg-slate-100 rounded-3xl p-6 md:p-8 text-white relative overflow-hidden border border-emerald-900/40">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-gilroyBold uppercase tracking-wider bg-emerald-300/20 text-gray-600 border border-emerald-200/30">
                <span className="w-2 h-2 rounded-full bg-gray-600 animate-pulse" />
                Ambassador Performance Hub
              </span>
              {Boolean(ambassadorInfo?.isStudent || ambassadorInfo?.school) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-gilroyBold uppercase tracking-wider bg-emerald-600 text-white shadow-xs">
                  🎓 Student Ambassador • {ambassadorInfo.school || "Verified Student"}
                </span>
              )}
            </div>
            <h2 className="font-gilroyBold text-3xl md:text-4xl text-gray-800 m-0 tracking-tight">
              Grow & Earn With Konnect
            </h2>
            <p className=" text-gray-600 m-0 max-w-xl leading-relaxed">
              Track your referral signups, brand campaigns, and live revenue milestones in real time.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="primary"
              onClick={() => setIsReferralDrawerOpen(true)}
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-gilroyBold h-12 px-5 rounded-2xl border-0 shadow-lg shadow-emerald-500/25 flex items-center gap-2 cursor-pointer transition-all hover:scale-[1.02]"
            >
              <Share2 size={16} />
              Copy Referral Links
            </Button>
            <Button
              type="default"
              onClick={() => {
                const targetEl = document.getElementById("latest-promo-post") || document.getElementById("submitted-promotional-posts");
                targetEl?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="bg-gray-800 hover:bg-white/20 text-white border border-white/20 font-gilroyBold h-12 px-5 rounded-2xl flex items-center gap-2 cursor-pointer backdrop-blur-md transition-all"
            >
              Latest Promos
            </Button>
          </div>
        </div>
      </div>

      {/* Promo Stats Grid */}
      <div className="relative">
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 no-scrollbar md:grid md:grid-cols-2 lg:grid-cols-4 md:overflow-visible">
          {/* Card 1: User Referred */}
          <div className="flex-shrink-0 w-[85%] md:w-auto snap-center bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="bg-emerald-100 text-emerald-700 p-3 rounded-2xl shadow-xs">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <Tag color="emerald" className="font-gilroyBold uppercase text-xs px-2.5 py-1 rounded-lg border-0 m-0 bg-emerald-50 text-emerald-700">
                  Active
                </Tag>
              </div>
              <h4 className="font-gilroyBold text-xl text-gray-900 m-0 mb-1">User Referred</h4>
              <div className="flex items-baseline justify-between gap-2 my-2">
                <p className="text-4xl font-gilroyBold text-gray-900 m-0 tracking-tight">
                  {performanceStats.userReferred ?? performanceStats.totalReferrals ?? 0}
                </p>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => setIsReferralDrawerOpen(true)}
                  className="bg-black hover:bg-gray-800 text-white rounded-xl font-gilroyBold flex items-center gap-1.5 px-3 py-1.5 cursor-pointer flex-shrink-0"
                >
                  <Share2 size={13} />
                  Share
                </Button>
              </div>
            </div>
            <p className=" text-gray-500 font-medium m-0 mt-3 border-t border-gray-100 pt-3">
              Verified user signups
            </p>
          </div>

          {/* Card 2: Group Account */}
          <div className="flex-shrink-0 w-[85%] md:w-auto snap-center bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="bg-blue-100 text-blue-700 p-3 rounded-2xl shadow-xs">
                  <Users className="w-6 h-6" />
                </div>
                <Tag color="blue" className="font-gilroyBold uppercase text-xs px-2.5 py-1 rounded-lg border-0 m-0 bg-blue-50 text-blue-700">
                  Business
                </Tag>
              </div>
              <h4 className="font-gilroyBold text-xl text-gray-900 m-0 mb-1">Group Account</h4>
              <p className="text-4xl font-gilroyBold text-gray-900 my-2 tracking-tight">
                {performanceStats.groupAccountReferred ?? performanceStats.groupAccountCount ?? 0}
              </p>
            </div>
            <p className=" text-gray-500 font-medium m-0 border-t border-gray-100 pt-3">
              Group & business accounts
            </p>
          </div>

          {/* Card 3: Ambassador */}
          <div className="flex-shrink-0 w-[85%] md:w-auto snap-center bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="bg-purple-100 text-purple-700 p-3 rounded-2xl shadow-xs">
                  <Award className="w-6 h-6" />
                </div>
                <Tag color="purple" className="font-gilroyBold uppercase text-xs px-2.5 py-1 rounded-lg border-0 m-0 bg-purple-50 text-purple-700">
                  Network
                </Tag>
              </div>
              <h4 className="font-gilroyBold text-xl text-gray-900 m-0 mb-1">Ambassadors</h4>
              <p className="text-4xl font-gilroyBold text-gray-900 my-2 tracking-tight">
                {performanceStats.ambassadorsReferred ?? performanceStats.ambassadorCount ?? 0}
              </p>
            </div>
            <p className=" text-gray-500 font-medium m-0 border-t border-gray-100 pt-3">
              Referred ambassadors
            </p>
          </div>

          {/* Card 4: Revenue (Target vs Actual) */}
          <div className="flex-shrink-0 w-[85%] md:w-auto snap-center bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="bg-amber-100 text-amber-700 p-3 rounded-2xl shadow-xs">
                  <DollarSign className="w-6 h-6" />
                </div>
                <Tag color="amber" className="font-gilroyBold uppercase text-xs px-2.5 py-1 rounded-lg border-0 m-0 bg-amber-50 text-amber-700">
                  Earnings
                </Tag>
              </div>
              <h4 className="font-gilroyBold text-xl text-gray-900 m-0 mb-1">Revenue</h4>
              <div className="my-2">
                <p className="text-2xl font-gilroyBold text-gray-900 m-0 tracking-tight">
                  ₦{(performanceStats.actualRevenue ?? performanceStats.totalEarnings ?? 0).toLocaleString()}
                </p>
                <p className=" font-medium text-gray-400 m-0 mt-0.5">
                  Target: ₦{(performanceStats.targetRevenue ?? 0).toLocaleString()}
                </p>
              </div>
            </div>
            {performanceStats.targetRevenue > 0 ? (
              <div className="border-t border-gray-100 pt-3">
                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.round(
                          ((performanceStats.actualRevenue ?? performanceStats.totalEarnings ?? 0) /
                            performanceStats.targetRevenue) *
                          100
                        )
                      )}%`,
                    }}
                  />
                </div>
              </div>
            ) : (
              <p className=" text-gray-500 font-medium m-0 border-t border-gray-100 pt-3">
                Target vs Actual points
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Submitted promotional posts */}
      <div id="submitted-promotional-posts" className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="bg-emerald-100 text-emerald-800 text-xs font-gilroyBold px-2.5 py-1 rounded-lg">
              {existingPosts.length} Submitted
            </span>
            <h3 className="font-gilroyBold text-2xl text-gray-900 m-0">
              Submitted Promotional Posts
            </h3>
          </div>
          <p className=" text-gray-600 m-0">
            Submit your active campaign post URLs and manage your verified submissions.
          </p>
        </div>
        <Button
          type="primary"
          onClick={() => setIsPostDrawerOpen(true)}
          className="bg-black hover:bg-gray-800 text-white font-gilroyBold h-12 px-6 rounded-2xl flex items-center gap-2 flex-shrink-0 cursor-pointer shadow-md hover:scale-[1.01] transition-all"
        >
          <FileText size={18} />
          Submit Post Links ({existingPosts.length})
        </Button>
      </div>

      {/* Today's Post / Campaign Task */}
      {Boolean(nextPost) && postItems.length > 0 && (
        <div id="latest-promo-post" className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="border-b border-gray-100 pb-4">
            <h3 className="font-gilroyBold text-2xl text-gray-900 m-0">
              {hasSubmittedToday ? "Next Campaign Task" : "Today's Campaign Task"}
            </h3>
            <p className=" text-gray-600 m-0 mt-1">
              Copy post caption with your referral link and download media assets directly below.
            </p>
          </div>
          <AdminCollapseList
            items={renderAmbassadorPostCollapseItems()}
            defaultActiveKey={[String(postItems[0]?.id || nextPost?.id || "post-0")]}
          />
        </div>
      )}

      {/* Community Support */}
      <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-gilroyBold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
              <span className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse" />
              Priority Support
            </span>
            <h4 className="font-gilroyBold text-2xl md:text-3xl text-gray-900 m-0 tracking-tight">
              Ambassador Support
            </h4>
            <p className="text-gray-600  m-0  leading-relaxed font-medium">
              Connect directly with our Ambassador Lead team on WhatsApp for fast approval & assistance.
            </p>
          </div>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black hover:bg-gray-800 text-white font-gilroyBold h-12 px-6 rounded-2xl flex items-center gap-2.5 cursor-pointer border-0 shrink-0 no-underline transition-all"
          >
            <MessageCircle size={20} className="fill-current text-white" />
            <span className="text-base">WhatsApp</span>
          </a>
      </div>

      {/* Bottom Sheet Drawer for Referral Links */}
      <Drawer
        placement="bottom"
        open={isReferralDrawerOpen}
        onClose={() => setIsReferralDrawerOpen(false)}
        height="auto"
        className="rounded-t-3xl"
        title={
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-emerald-600" />
            <span className="font-gilroyBold text-2xl text-gray-900">Your Referral Links</span>
          </div>
        }
      >
        <div className="p-2 space-y-4 pb-6">
          <p className=" text-gray-600">
            Select and copy any referral link tailored for your promotion:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: "Marketplace Customer Link", type: "marketplace" },
              { label: "Business Partner Link", type: "business" },
              { label: "Ambassador Link", type: "ambassador" },
              { label: "Wise9ja Campaign Link", type: "wise9ja" },
            ].map((item) => {
              const link = getReferralLink(ambassadorInfo.accountId || "ACCOUNT_ID", item.type);
              return (
                <div key={item.type} className="p-4 bg-gray-50/80 border border-gray-200 rounded-2xl space-y-2">
                  <p className="font-gilroyBold text-gray-900  m-0">{item.label}</p>
                  <div className="flex gap-2">
                    <Input value={link} readOnly className="bg-white font-mono rounded-xl " />
                    <Button
                      type="primary"
                      className="bg-black hover:bg-gray-800 text-white font-gilroyBold rounded-xl cursor-pointer h-10 px-4"
                      onClick={() => {
                        navigator.clipboard.writeText(link);
                        message.success(`${item.label} copied!`);
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Drawer>

      {/* Bottom Sheet Drawer for Submitted Post Links */}
      <Drawer
        placement="bottom"
        open={isPostDrawerOpen}
        onClose={() => setIsPostDrawerOpen(false)}
        height="85vh"
        className="rounded-t-3xl"
        title={
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            <span className="font-gilroyBold text-2xl text-gray-900">Submit & Manage Promotional Post Links</span>
          </div>
        }
      >
        <div className="p-2 space-y-6 pb-6 overflow-y-auto max-h-[75vh] no-scrollbar">
          {postItems.length > 0 && (
            <Card className="border border-emerald-200 rounded-3xl p-5 shadow-xs bg-emerald-50">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <h4 className="font-gilroyBold text-xl text-gray-900 m-0">Next campaign content</h4>
              </div>
              <p className="text-gray-600 mb-4">
                The post message and available assets below are supplied by the Konnect admin team.
              </p>
              <AdminCollapseList
                items={renderAmbassadorPostCollapseItems()}
                defaultActiveKey={[String(postItems[0]?.id || nextPost?.id || "post-0")]}
              />
            </Card>
          )}

          {/* Submit New Posts Form */}
          <Card className="border border-gray-200/80 rounded-3xl p-5 shadow-xs bg-white">
            <h4 className="font-gilroyBold text-2xl text-gray-900 mb-1">
              Submit New Post Links
            </h4>
            <p className="text-gray-500 mb-4 ">
              Paste URLs of your promotional posts. New submissions are approved automatically.
            </p>

            <Form form={form} layout="vertical" onFinish={handlePostSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Form.Item
                  name="instagram"
                  label={
                    <span className="flex items-center gap-2  font-gilroyBold text-gray-900">
                      <Instagram className="w-4 h-4 text-pink-600" /> Instagram Post / Reel URL
                    </span>
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) return Promise.resolve();
                        const val = value.trim().toLowerCase();
                        if (!val.startsWith("https://")) {
                          return Promise.reject(new Error("URL must start with https://"));
                        }
                        if (!val.includes("instagram.com")) {
                          return Promise.reject(new Error("Must contain instagram.com"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="https://www.instagram.com/p/..." className="py-2.5 rounded-xl " />
                </Form.Item>

                <Form.Item
                  name="tiktok"
                  label={
                    <span className="flex items-center gap-2  font-gilroyBold text-gray-900">
                      <Video className="w-4 h-4 text-black" /> TikTok Video URL
                    </span>
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) return Promise.resolve();
                        const val = value.trim().toLowerCase();
                        if (!val.startsWith("https://")) {
                          return Promise.reject(new Error("URL must start with https://"));
                        }
                        if (!val.includes("tiktok.com")) {
                          return Promise.reject(new Error("Must contain tiktok.com"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="https://www.tiktok.com/@..." className="py-2.5 rounded-xl " />
                </Form.Item>

                <Form.Item
                  name="twitter"
                  label={
                    <span className="flex items-center gap-2  font-gilroyBold text-gray-900">
                      <Twitter className="w-4 h-4 text-blue-500" /> Twitter / X Post URL
                    </span>
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) return Promise.resolve();
                        const val = value.trim().toLowerCase();
                        if (!val.startsWith("https://")) {
                          return Promise.reject(new Error("URL must start with https://"));
                        }
                        if (!val.includes("x.com") && !val.includes("twitter.com")) {
                          return Promise.reject(new Error("Must contain x.com or twitter.com"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="https://x.com/..." className="py-2.5 rounded-xl " />
                </Form.Item>

                <Form.Item
                  name="youtube"
                  label={
                    <span className="flex items-center gap-2  font-gilroyBold text-gray-900">
                      <Youtube className="w-4 h-4 text-red-600" /> YouTube Video / Short URL
                    </span>
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) return Promise.resolve();
                        const val = value.trim().toLowerCase();
                        if (!val.startsWith("https://")) {
                          return Promise.reject(new Error("URL must start with https://"));
                        }
                        if (!val.includes("youtube.com") && !val.includes("youtu.be")) {
                          return Promise.reject(new Error("Must contain youtube.com or youtu.be"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="https://www.youtube.com/watch?v=..." className="py-2.5 rounded-xl " />
                </Form.Item>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                className="bg-black hover:bg-gray-800 text-white font-gilroyBold px-6 rounded-2xl mt-1 cursor-pointer"
              >
                Submit Posts for Verification
              </Button>
            </Form>
          </Card>

          {/* Submitted Links List */}
          <Card className="border border-gray-200/80 rounded-3xl p-5 shadow-xs bg-white">
            <h4 className="font-gilroyBold text-2xl text-gray-900 mb-1">
              Your Submitted Post Links ({existingPosts.length})
            </h4>
            <p className="text-gray-500 mb-4 ">
              Manage your submitted post links. Preview or delete links below.
            </p>

            {existingPosts.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-200">
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 font-gilroyBold ">No post links submitted yet</p>
                <p className="text-gray-400 text-md">Use the form above to submit your promotional links. They will appear here immediately.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {existingPosts.map((item, idx) => {
                  const url = typeof item === "object" ? item.postUrl : item;
                  const itemKey = typeof item === "object" ? item.id || url : url;
                  const status = typeof item === "object" ? item.status || "PENDING" : "PENDING";
                  const createdAt = typeof item === "object" && item.createdAt ? new Date(item.createdAt).toLocaleDateString() : null;
                  const meta = getPlatformMeta(url);

                  return (
                    <div
                      key={itemKey || idx}
                      className="p-3.5 bg-gray-50 border border-gray-200/80 rounded-2xl flex items-center justify-between gap-3 hover:border-gray-300 transition-all"
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className="p-2.5 bg-white rounded-xl border border-gray-200 shadow-xs flex-shrink-0">
                          {meta.icon}
                        </div>
                        <div className="overflow-hidden space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900 font-gilroyBold ">{meta.name}</span>
                            <Tag color={status === "APPROVED" ? "green" : status === "REJECTED" ? "red" : "gold"} className="m-0 text-xs font-gilroyBold rounded-md">
                              {status === "APPROVED" ? "Approved" : status === "REJECTED" ? "Rejected" : "Pending Review"}
                            </Tag>
                            {createdAt && <span className="text-gray-400 text-xs">• {createdAt}</span>}
                          </div>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 font-mono text-md truncate block hover:underline"
                          >
                            {url}
                          </a>
                          {typeof item === "object" && item.adminNote && (
                            <p className="text-red-500 text-xs italic m-0">Note: {item.adminNote}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-600 hover:text-black bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-all"
                          title="Open link"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>

                        <Popconfirm
                          title="Delete Post Link"
                          description="Are you sure you want to remove this post link?"
                          onConfirm={() => handleDeletePost(item)}
                          okText="Yes, Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            danger
                            type="default"
                            loading={deletingPost === itemKey}
                            icon={<Trash2 className="w-4 h-4 text-red-600" />}
                            className="flex items-center justify-center p-2 rounded-xl border-red-200 bg-red-50 hover:bg-red-100 cursor-pointer"
                          />
                        </Popconfirm>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </Drawer>
    </div>
  );
};

export default AmbassadorPerformance;
